import { Injectable, Injector } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MetadataChangeService } from '../services/metadata-change.service';
import { MetadataChangeRequest } from '../../../awsAppSync/API';
import {
  METADATA_CHANGE_STATE,
  METADATA_STATUS_PENDING,
  METADATA_STATUS_ACCEPTED,
  METADATA_STATUS_DECLINED_UPPER,
  METADATA_STATUS_EDITED,
  METADATA_LOAD_FAILED,
} from '../../shared/constants';

// Actions
export class LoadMetadataChanges {
  static readonly type = '[MetadataChange] Load';
  constructor(
    public primaryCompanyId: string,
    public surveyId: string,
    public status?: string,
  ) {}
}

export class AcceptMetadataChange {
  static readonly type = '[MetadataChange] Accept';
  constructor(
    public primaryCompanyId: string,
    public surveyId: string,
    public changeId: string,
  ) {}
}

export class DeclineMetadataChange {
  static readonly type = '[MetadataChange] Decline';
  constructor(
    public primaryCompanyId: string,
    public surveyId: string,
    public changeId: string,
  ) {}
}

export class EditMetadataChange {
  static readonly type = '[MetadataChange] Edit';
  constructor(
    public primaryCompanyId: string,
    public surveyId: string,
    public changeId: string,
    public finalValue: string,
  ) {}
}

export class ClearMetadataChanges {
  static readonly type = '[MetadataChange] Clear';
}

// State Model
export interface MetadataChangeStateModel {
  changes: MetadataChangeRequest[];
  loading: boolean;
  error: string | null;
  actionInProgress: string | null;
}

@State<MetadataChangeStateModel>({
  name: METADATA_CHANGE_STATE,
  defaults: {
    changes: [],
    loading: false,
    error: null,
    actionInProgress: null,
  },
})
@Injectable()
export class MetadataChangeState {
  constructor(private injector: Injector) {}

  private get metadataChangeService(): MetadataChangeService {
    return this.injector.get(MetadataChangeService);
  }

  // Selectors
  @Selector()
  static getChanges(state: MetadataChangeStateModel): MetadataChangeRequest[] {
    return state.changes;
  }

  @Selector()
  static getLoading(state: MetadataChangeStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getError(state: MetadataChangeStateModel): string | null {
    return state.error;
  }

  @Selector()
  static getActionInProgress(
    state: MetadataChangeStateModel,
  ): string | null {
    return state.actionInProgress;
  }

  @Selector()
  static getPendingChanges(
    state: MetadataChangeStateModel,
  ): MetadataChangeRequest[] {
    return state.changes.filter((c) => c.status === METADATA_STATUS_PENDING);
  }

  @Selector()
  static getResolvedChanges(
    state: MetadataChangeStateModel,
  ): MetadataChangeRequest[] {
    return state.changes.filter(
      (c) =>
        c.status === METADATA_STATUS_ACCEPTED ||
        c.status === METADATA_STATUS_DECLINED_UPPER ||
        c.status === METADATA_STATUS_EDITED,
    );
  }

  // Actions
  @Action(LoadMetadataChanges)
  async loadMetadataChanges(
    { patchState }: StateContext<MetadataChangeStateModel>,
    { primaryCompanyId, surveyId, status }: LoadMetadataChanges,
  ): Promise<void> {
    patchState({ loading: true, error: null });
    try {
      const result =
        await this.metadataChangeService.getMetadataChangesForSurvey(
          primaryCompanyId,
          surveyId,
          status,
        );
      if (result === null) {
        patchState({
          changes: [],
          loading: false,
          error: METADATA_LOAD_FAILED,
        });
      } else {
        patchState({
          changes: result,
          loading: false,
          error: null,
        });
      }
    } catch (error) {
      console.error(METADATA_LOAD_FAILED, error);
      patchState({ loading: false, error: METADATA_LOAD_FAILED });
    }
  }

  @Action(AcceptMetadataChange)
  async acceptMetadataChange(
    { getState, patchState }: StateContext<MetadataChangeStateModel>,
    { primaryCompanyId, surveyId, changeId }: AcceptMetadataChange,
  ): Promise<void> {
    patchState({ actionInProgress: changeId });
    try {
      const updated = await this.metadataChangeService.acceptMetadataChange({
        primaryCompanyId,
        surveyId,
        changeId,
      });
      if (updated) {
        const changes = getState().changes.map((c) =>
          c.id === changeId ? { ...c, ...updated } : c,
        );
        patchState({ changes, actionInProgress: null });
      } else {
        patchState({ actionInProgress: null });
      }
    } catch (error) {
      console.error('Failed to accept metadata change', error);
      patchState({ actionInProgress: null });
    }
  }

  @Action(DeclineMetadataChange)
  async declineMetadataChange(
    { getState, patchState }: StateContext<MetadataChangeStateModel>,
    { primaryCompanyId, surveyId, changeId }: DeclineMetadataChange,
  ): Promise<void> {
    patchState({ actionInProgress: changeId });
    try {
      const updated = await this.metadataChangeService.declineMetadataChange({
        primaryCompanyId,
        surveyId,
        changeId,
      });
      if (updated) {
        const changes = getState().changes.map((c) =>
          c.id === changeId ? { ...c, ...updated } : c,
        );
        patchState({ changes, actionInProgress: null });
      } else {
        patchState({ actionInProgress: null });
      }
    } catch (error) {
      console.error('Failed to decline metadata change', error);
      patchState({ actionInProgress: null });
    }
  }

  @Action(EditMetadataChange)
  async editMetadataChange(
    { getState, patchState }: StateContext<MetadataChangeStateModel>,
    { primaryCompanyId, surveyId, changeId, finalValue }: EditMetadataChange,
  ): Promise<void> {
    patchState({ actionInProgress: changeId });
    try {
      const updated = await this.metadataChangeService.editMetadataChange({
        primaryCompanyId,
        surveyId,
        changeId,
        finalValue,
      });
      if (updated) {
        const changes = getState().changes.map((c) =>
          c.id === changeId ? { ...c, ...updated } : c,
        );
        patchState({ changes, actionInProgress: null });
      } else {
        patchState({ actionInProgress: null });
      }
    } catch (error) {
      console.error('Failed to edit metadata change', error);
      patchState({ actionInProgress: null });
    }
  }

  @Action(ClearMetadataChanges)
  clearMetadataChanges(
    { setState }: StateContext<MetadataChangeStateModel>,
  ): void {
    setState({
      changes: [],
      loading: false,
      error: null,
      actionInProgress: null,
    });
  }
}
